/**
 * HeroBackground — Phase v5.3 §9.3 hero ambience layer.
 *
 * Sits absolute-positioned inside `.hero-root`, behind the hero
 * content. Contributes three subtle motion layers:
 *
 *   1. Cursor-reactive SVG grid. Grid intersections within ~200 px
 *      of the pointer brighten and grow a small indigo dot; a light
 *      "halo" band highlights the nearest row / column pair.
 *
 *   2. Canvas ambient pulses. Every 4–6 s a small glowing dot
 *      spawns and travels along a random grid line (horizontal or
 *      vertical) for ~2 s before fading. 2–3 concurrent at most.
 *
 *   3. Faint algorithmic values drifting in the background at
 *      ~8 % opacity. Rotates through eight math-notation strings.
 *      Central 400 px-wide column is kept clear so the headline
 *      stays readable.
 *
 * All layers gate on `prefers-reduced-motion` (frozen positions,
 * no listeners, no rAF loops). Touch devices bypass the cursor
 * layer (`hover: none`). Lower-end devices (`window.innerWidth
 * < 900`) drop the canvas pulses and reduce values to 6.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";

/* Math-notation strings — copy-scrub-safe. Documented in
 * docs/copy-scrub.md under "Phase v5.3 hero-background". */
const AMBIENT_VALUES = [
    "p50 → 0.34",
    "σ = 0.128",
    "n = 400",
    "∂C/∂t",
    "E[ΔD] · h₃",
    "KL(P‖Q) → 0.021",
    "posterior ≈ N(μ, σ²)",
    "λ_max ≈ 0.07",
];

/* Grid pitch in CSS pixels. Matches the existing `.hero-grid`
 * background repeat so the two layers stay aligned. */
const GRID = 64;

/* Cursor-halo radius. Grid intersections closer than this pulse. */
const CURSOR_RADIUS = 200;

export default function HeroBackground() {
    const reduce = useReducedMotion();
    const wrapRef = useRef(null);
    const svgRef = useRef(null);
    const canvasRef = useRef(null);
    const [size, setSize] = useState({ w: 0, h: 0 });

    // Degrade on narrow / low-power devices.
    const [lowPower, setLowPower] = useState(false);
    useEffect(() => {
        const check = () => setLowPower(window.innerWidth < 900);
        check();
        window.addEventListener("resize", check);
        return () => window.removeEventListener("resize", check);
    }, []);

    // Detect touch-only devices — skip cursor layer entirely.
    const isFinePointer = useMemo(() => {
        if (typeof window === "undefined") return false;
        return window.matchMedia?.("(hover: hover) and (pointer: fine)")?.matches ?? false;
    }, []);

    /* ── Track wrapper size ───────────────────────────────── */
    useEffect(() => {
        if (!wrapRef.current) return;
        const el = wrapRef.current;
        const measure = () => {
            const r = el.getBoundingClientRect();
            setSize({ w: r.width, h: r.height });
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        window.addEventListener("resize", measure);
        return () => { ro.disconnect(); window.removeEventListener("resize", measure); };
    }, []);

    /* ── Cursor-reactive grid ─────────────────────────────── */
    const cursorRef = useRef({ x: -9999, y: -9999, live: false });
    const rafRef = useRef(0);
    useEffect(() => {
        if (reduce || !isFinePointer || !wrapRef.current) return;
        const el = wrapRef.current;
        const onMove = (e) => {
            const r = el.getBoundingClientRect();
            cursorRef.current.x = e.clientX - r.left;
            cursorRef.current.y = e.clientY - r.top;
            cursorRef.current.live = true;
            if (!rafRef.current) rafRef.current = requestAnimationFrame(paint);
        };
        const onLeave = () => {
            cursorRef.current.live = false;
            cursorRef.current.x = -9999;
            cursorRef.current.y = -9999;
            if (!rafRef.current) rafRef.current = requestAnimationFrame(paint);
        };
        const paint = () => {
            rafRef.current = 0;
            const svg = svgRef.current;
            if (!svg) return;
            const nodes = svg.querySelectorAll("[data-node]");
            const rows  = svg.querySelectorAll("[data-row]");
            const cols  = svg.querySelectorAll("[data-col]");
            const cx = cursorRef.current.x;
            const cy = cursorRef.current.y;

            // Node highlighting — nearest ~5 grow an indigo dot proportional to distance.
            nodes.forEach((n) => {
                const nx = +n.getAttribute("cx");
                const ny = +n.getAttribute("cy");
                const d = Math.hypot(nx - cx, ny - cy);
                if (d < CURSOR_RADIUS && cursorRef.current.live) {
                    const t = 1 - d / CURSOR_RADIUS;      // 0..1
                    n.setAttribute("r", (0.6 + 2.4 * t).toFixed(2));
                    n.style.opacity = String(0.15 + 0.75 * t);
                } else {
                    n.setAttribute("r", "0.6");
                    n.style.opacity = "0";
                }
            });

            // Row / column halo — brightest row + col nearest the cursor.
            const rowIdx = Math.round(cy / GRID);
            const colIdx = Math.round(cx / GRID);
            rows.forEach((r) => {
                const idx = +r.getAttribute("data-row");
                const dr  = Math.abs(idx - rowIdx);
                const t = cursorRef.current.live ? Math.max(0, 1 - dr / 2.5) : 0;
                r.style.opacity = String(0.08 + 0.32 * t);
                r.setAttribute("stroke-width", String(0.5 + 0.8 * t));
            });
            cols.forEach((c) => {
                const idx = +c.getAttribute("data-col");
                const dc  = Math.abs(idx - colIdx);
                const t = cursorRef.current.live ? Math.max(0, 1 - dc / 2.5) : 0;
                c.style.opacity = String(0.08 + 0.32 * t);
                c.setAttribute("stroke-width", String(0.5 + 0.8 * t));
            });
        };
        el.addEventListener("mousemove", onMove);
        el.addEventListener("mouseleave", onLeave);
        return () => {
            el.removeEventListener("mousemove", onMove);
            el.removeEventListener("mouseleave", onLeave);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = 0;
        };
    }, [reduce, isFinePointer, size.w, size.h]);

    /* ── Canvas ambient pulses ────────────────────────────── */
    useEffect(() => {
        if (reduce || lowPower || !canvasRef.current || size.w === 0 || size.h === 0) return;
        const canvas = canvasRef.current;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width  = size.w * dpr;
        canvas.height = size.h * dpr;
        const ctx = canvas.getContext("2d");
        ctx.scale(dpr, dpr);

        const pulses = [];   // { axis, x, y, t0, duration, span }
        let rafId = 0;
        let spawnTimer = 0;

        const spawn = () => {
            if (pulses.length >= 3) return;
            const axis = Math.random() < 0.5 ? "h" : "v";
            const cols = Math.floor(size.w / GRID);
            const rows = Math.floor(size.h / GRID);
            if (axis === "h") {
                pulses.push({
                    axis,
                    y: (Math.floor(Math.random() * rows) + 1) * GRID,
                    t0: performance.now(),
                    duration: 2000,
                    span: size.w,
                });
            } else {
                pulses.push({
                    axis,
                    x: (Math.floor(Math.random() * cols) + 1) * GRID,
                    t0: performance.now(),
                    duration: 2000,
                    span: size.h,
                });
            }
        };
        const scheduleSpawn = () => {
            const wait = 4000 + Math.random() * 2000;   // 4–6 s
            spawnTimer = window.setTimeout(() => {
                spawn();
                scheduleSpawn();
            }, wait);
        };

        // Kick off with one visible pulse ~300 ms after mount.
        const openTimer = window.setTimeout(() => { spawn(); scheduleSpawn(); }, 300);

        const draw = (now) => {
            ctx.clearRect(0, 0, size.w, size.h);
            for (let i = pulses.length - 1; i >= 0; i--) {
                const p = pulses[i];
                const t = (now - p.t0) / p.duration;   // 0..1
                if (t >= 1.1) { pulses.splice(i, 1); continue; }
                const progress = Math.min(1, t);
                // Alpha: fade in over first 15 %, out over last 15 % of life.
                const alpha = t < 0.15 ? (t / 0.15) * 0.4
                            : t > 0.85 ? Math.max(0, (1 - t) / 0.15) * 0.4
                            : 0.4;
                if (p.axis === "h") {
                    const cx = progress * p.span;
                    const grad = ctx.createRadialGradient(cx, p.y, 0, cx, p.y, 45);
                    grad.addColorStop(0,   `rgba(26, 35, 126, ${alpha})`);
                    grad.addColorStop(0.7, `rgba(26, 35, 126, ${alpha * 0.15})`);
                    grad.addColorStop(1,   "rgba(26, 35, 126, 0)");
                    ctx.fillStyle = grad;
                    ctx.beginPath();
                    ctx.arc(cx, p.y, 45, 0, Math.PI * 2);
                    ctx.fill();
                } else {
                    const cy = progress * p.span;
                    const grad = ctx.createRadialGradient(p.x, cy, 0, p.x, cy, 45);
                    grad.addColorStop(0,   `rgba(26, 35, 126, ${alpha})`);
                    grad.addColorStop(0.7, `rgba(26, 35, 126, ${alpha * 0.15})`);
                    grad.addColorStop(1,   "rgba(26, 35, 126, 0)");
                    ctx.fillStyle = grad;
                    ctx.beginPath();
                    ctx.arc(p.x, cy, 45, 0, Math.PI * 2);
                    ctx.fill();
                }
            }
            rafId = requestAnimationFrame(draw);
        };
        rafId = requestAnimationFrame(draw);

        return () => {
            cancelAnimationFrame(rafId);
            clearTimeout(openTimer);
            clearTimeout(spawnTimer);
        };
    }, [reduce, lowPower, size.w, size.h]);

    /* ── Ambient algorithmic values ───────────────────────── */
    const items = useMemo(() => {
        // Deterministic pseudo-random layout so the values don't jiggle
        // on every re-render, but placement varies between sessions.
        const count = lowPower ? 6 : 10;
        const seed = 0x9E3779B9 ^ Math.floor(Date.now() / 60000);   // stable per-minute
        let s = seed;
        const rand = () => {
            s = (s * 1664525 + 1013904223) >>> 0;
            return (s & 0xFFFFFFFF) / 0x100000000;
        };
        const out = [];
        // Center corridor to keep clear (400 px wide, centered).
        const CORRIDOR = 400;
        for (let i = 0; i < count; i++) {
            const text = AMBIENT_VALUES[(i + seed % AMBIENT_VALUES.length) % AMBIENT_VALUES.length];
            // Distribute so we skip the central corridor.
            const side  = i % 2 === 0 ? "left" : "right";
            const relY  = 12 + rand() * 76;                     // 12–88 %
            const relX  = side === "left"
                ? rand() * 42                                    // 0–42 %
                : 58 + rand() * 42;                              // 58–100 %
            out.push({
                text,
                relX, relY,
                driftPhase: rand() * Math.PI * 2,
                driftPeriod: 8000 + rand() * 4000,               // 8–12 s
                fadePhase: rand() * Math.PI * 2,
                fadePeriod: 6000 + rand() * 2000,                // 6–8 s
                corridor: CORRIDOR,
            });
        }
        return out;
    }, [lowPower]);

    return (
        <div
            ref={wrapRef}
            className="hero-background"
            aria-hidden="true"
            data-testid="hero-background"
            style={{
                position: "absolute",
                inset: 0,
                pointerEvents: "auto",
                overflow: "hidden",
                zIndex: 1,
            }}
        >
            {/* Cursor-reactive SVG grid overlay */}
            {size.w > 0 && size.h > 0 && (
                <svg
                    ref={svgRef}
                    width={size.w}
                    height={size.h}
                    className="pointer-events-none absolute inset-0"
                    style={{ zIndex: 2 }}
                >
                    {/* Vertical column lines */}
                    {Array.from({ length: Math.ceil(size.w / GRID) + 1 }).map((_, i) => (
                        <line
                            key={`c${i}`}
                            data-col={i}
                            x1={i * GRID} x2={i * GRID}
                            y1={0} y2={size.h}
                            stroke="#FFFFFF"
                            style={{ opacity: 0.08, transition: "opacity 300ms ease-out, stroke-width 300ms ease-out" }}
                            strokeWidth={0.5}
                        />
                    ))}
                    {/* Horizontal row lines */}
                    {Array.from({ length: Math.ceil(size.h / GRID) + 1 }).map((_, i) => (
                        <line
                            key={`r${i}`}
                            data-row={i}
                            x1={0} x2={size.w}
                            y1={i * GRID} y2={i * GRID}
                            stroke="#FFFFFF"
                            style={{ opacity: 0.08, transition: "opacity 300ms ease-out, stroke-width 300ms ease-out" }}
                            strokeWidth={0.5}
                        />
                    ))}
                    {/* Intersection nodes — grow near the cursor */}
                    {isFinePointer && Array.from({ length: Math.ceil(size.w / GRID) + 1 }).flatMap((_, i) =>
                        Array.from({ length: Math.ceil(size.h / GRID) + 1 }).map((__, j) => (
                            <circle
                                key={`n${i}-${j}`}
                                data-node
                                cx={i * GRID}
                                cy={j * GRID}
                                r={0.6}
                                fill="#1A237E"
                                style={{ opacity: 0, transition: "opacity 300ms ease-out, r 220ms ease-out" }}
                            />
                        ))
                    )}
                </svg>
            )}

            {/* Canvas pulse layer */}
            {!reduce && !lowPower && size.w > 0 && (
                <canvas
                    ref={canvasRef}
                    style={{
                        position: "absolute", inset: 0,
                        width: size.w, height: size.h,
                        pointerEvents: "none",
                        zIndex: 3,
                    }}
                    data-testid="hero-background-pulses"
                />
            )}

            {/* Ambient math-notation values */}
            <div className="pointer-events-none absolute inset-0" style={{ zIndex: 4 }} data-testid="hero-background-values">
                {items.map((it, i) => (
                    <AmbientValue key={i} item={it} reduce={reduce} />
                ))}
            </div>
        </div>
    );
}

/* Single drifting value — CSS animation + inline sine easing. */
function AmbientValue({ item, reduce }) {
    const ref = useRef(null);
    useEffect(() => {
        if (reduce) return;
        const el = ref.current;
        if (!el) return;
        let raf = 0;
        const start = performance.now();
        const step = (now) => {
            const t = now - start;
            const drift = Math.sin(item.driftPhase + (t / item.driftPeriod) * Math.PI * 2);
            const wander = Math.cos(item.driftPhase + (t / item.driftPeriod) * Math.PI * 2 * 0.7);
            el.style.transform = `translate(${(drift * 4).toFixed(2)}px, ${(wander * 4).toFixed(2)}px)`;
            const fade = 0.5 + 0.5 * Math.sin(item.fadePhase + (t / item.fadePeriod) * Math.PI * 2);
            el.style.opacity = String(0.02 + 0.08 * fade);
            raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [item, reduce]);

    return (
        <span
            ref={ref}
            style={{
                position: "absolute",
                left: `${item.relX}%`,
                top:  `${item.relY}%`,
                color: "rgba(255, 255, 255, 0.08)",
                fontFamily: "'DM Mono', 'IBM Plex Mono', ui-monospace, monospace",
                fontSize: 12,
                letterSpacing: "0.02em",
                whiteSpace: "nowrap",
                userSelect: "none",
                willChange: "transform, opacity",
                opacity: reduce ? 0.06 : 0.02,
            }}
        >
            {item.text}
        </span>
    );
}
